// src/context/PresenceContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';

import { SocketContext } from './socket';
import { AuthContext } from './AuthContext';

const STATUS_RANK = {
  online: 3,
  idle: 2,
  away: 1,
  offline: 0,
};

const OFFLINE_GRACE_MS = 5 * 60 * 1000; // 5 minutes

const PresenceContext = createContext(null);

export const PresenceProvider = ({ children }) => {
  const [presenceMap, setPresenceMap] = useState({});
  const [orbitList, setOrbitList] = useState([]);
  const { presence } = useContext(SocketContext) || {};
  const { player } = useContext(AuthContext);
  const stateRef = useRef({});

  useEffect(() => {
    stateRef.current = presenceMap;
  }, [presenceMap]);

  const mergePresence = useCallback((prev, p, overwrite = false) => {
    if (!p) return prev;
    const key =
      p.playerId || p._id || p.mongoId || p.accountId || p.handle || p.id;
    if (!key) return prev;
    const next = { ...prev };
    const existing = next[key];
    const incomingLastSeen = p.lastSeen ?? p.lastSeenAt ?? p.timestamp ?? null;
    const existingLastSeen =
      existing?.lastSeen ?? existing?.lastSeenAt ?? existing?.timestamp ?? null;

    // Ignore stale payloads
    if (
      existing &&
      incomingLastSeen != null &&
      existingLastSeen != null &&
      existingLastSeen > incomingLastSeen
    ) {
      return next;
    }

    const merged = overwrite ? p : { ...(existing || {}), ...p };

    // Track wentOfflineAt for grace window
    if (merged.status === 'offline') {
      merged.wentOfflineAt =
        merged.wentOfflineAt || merged.lastSeen || Date.now();
    } else {
      merged.wentOfflineAt = null;
    }

    next[key] = merged;
    return next;
  }, []);

  const applySnapshot = useCallback(
    (snapshot = []) => {
      const snapArray = Array.isArray(snapshot)
        ? snapshot
        : snapshot?.sessions
        ? Object.values(snapshot.sessions)
        : [];
      setPresenceMap((prev) => {
        let next = { ...prev };
        snapArray.forEach((p) => {
          next = mergePresence(next, p, true);
        });
        const sorted = {};
        Object.keys(next)
          .sort()
          .forEach((key) => {
            sorted[key] = next[key];
          });
        return sorted;
      });
    },
    [mergePresence],
  );

  const applyDiff = useCallback(
    (diff) => {
      if (!diff) return;
      const { joins = [], updates = [], leaves = [] } = diff;

      setPresenceMap((prev) => {
        const base = { ...prev };
        const getKey = (playerObj) =>
          playerObj?.playerId ||
          playerObj?._id ||
          playerObj?.mongoId ||
          playerObj?.accountId ||
          playerObj?.handle;

        const upsert = (p) => {
          const key = getKey(p);
          if (!key) return;
          Object.assign(base, mergePresence(base, p, false));
        };

        joins.forEach(upsert);
        updates.forEach(upsert);
        leaves.forEach((p) => {
          const key = getKey(p);
          if (key && base[key]) {
            base[key] = {
              ...(base[key] || {}),
              status: 'offline',
              wentOfflineAt: Date.now(),
            };
          }
        });

        const sorted = {};
        Object.keys(base)
          .sort()
          .forEach((key) => {
            sorted[key] = base[key];
          });

        return sorted;
      });
    },
    [mergePresence],
  );

  useEffect(() => {
    const playerId =
      player?.data?.player?.playerId ||
      player?.playerId ||
      player?.data?.player?._id ||
      player?._id ||
      null;
    if (!presence || !playerId) return;

    const handleSnapshot = (snapshot) => applySnapshot(snapshot);
    const handleDiff = (diff) => applyDiff(diff);

    presence.onSnapshot(handleSnapshot);
    presence.onDiff(handleDiff);

    return () => {
      presence.offSnapshot(handleSnapshot);
      presence.offDiff(handleDiff);
    };
  }, [presence, player, applySnapshot, applyDiff]);

  useEffect(() => {
    const list = Object.values(presenceMap)
      .filter((p) => {
        if (!p || !p.playerId || !p.status) return false;
        if (p.status !== 'offline') return true;
        const offAt = p.wentOfflineAt || p.lastSeen;
        return offAt && Date.now() - offAt <= OFFLINE_GRACE_MS;
      })
      .sort((a, b) => {
        const ar = STATUS_RANK[a.status] || 0;
        const br = STATUS_RANK[b.status] || 0;
        if (br !== ar) return br - ar;
        return (b.lastSeen || 0) - (a.lastSeen || 0);
      });

    setOrbitList(list);
  }, [presenceMap]);

  const value = {
    presenceMap,
    orbitList,
  };

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
};

export const usePresence = () => {
  const ctx = useContext(PresenceContext);
  if (!ctx) {
    console.warn('usePresence() called outside PresenceProvider');
    return {
      presenceMap: {},
      orbitList: [],
    };
  }
  return ctx;
};
